import RecentInspectionsList from '@components/Home/RecentInspectionsList';
import InspectionFilterBar from '@components/Inspections/InspectionFilterBar';
import Header from '@components/Shared/Layout/Header';
import {
  hasFilters, type InspectionFilters, noFilters, readFilters, readPage, toFilterArgs, writeFilters,
} from '@lib/inspections/filters';
import { useLocalization } from '@lib/localization';
import { Box, Button, Pagination, Paper, Typography } from '@mui/material';
import { useListInspectionsQuery } from '@store/api/inspectionEndpoints';
import { useLoading } from '@store/hooks';
import { useDebounce } from '@store/hooks/useDebounce';
import { useToday } from '@store/hooks/useToday';
import { gray } from '@theme/primitives';
import dayjs from 'dayjs';
import { SearchX } from 'lucide-react';
import { useEffect, useId, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

const ITEMS_PER_PAGE = 10;

/** How long a page change may take before the rows on screen are marked as stale. */
const PAGING_INDICATOR_DELAY = 300;

/**
 * Every inspection the reader may read, newest first, from `GET /v1/inspections`, which
 * decides what each role sees — the list Home previews, at length, narrowed by the toolbar.
 * The breadcrumb names the page, so the list carries no heading of its own and the toolbar
 * stands where it would.
 *
 * The filters and the page live in the URL, so a filtered list can be linked to, and coming
 * back from an inspection lands on the same rows. They are written with `replace`: refining
 * a filter is not a place the browser's Back should step through. Any change to the filters
 * goes back to the first page, since the page the reader was on may no longer exist.
 *
 * The page is the unit of fetching: each page of each set of filters is its own request and
 * its own cache entry. Rows are drawn by the list Home draws, so following the heading out
 * of Home lands on the same thing, only more of it.
 */
const InspectionsPage = () => {
  const { t } = useLocalization();
  const { show, hide } = useLoading();
  const componentId = useId();

  const [searchParams, setSearchParams] = useSearchParams();

  const filters = useMemo(() => readFilters(searchParams), [searchParams]);
  const page = readPage(searchParams);
  // A preset ends today, and a page left open past midnight moves with the calendar.
  const today = useToday();
  const filterArgs = useMemo(() => toFilterArgs(filters, dayjs(today)), [filters, today]);
  const filtered = hasFilters(filters);

  // `data` outlives an argument change — the rows of the previous page or filters stay until
  // the next arrive.
  const { data, currentData, error, isLoading, isFetching, refetch } =
    useListInspectionsQuery({ page, limit: ITEMS_PER_PAGE, ...filterArgs });

  // A failure of the filters and page asked for is a state of its own, never the rows of the
  // last ones that answered: those would stay on screen undimmed, under a pager that has moved
  // on, and a first load that failed would say there are no inspections. The failure stays on
  // the entry while a retry is on its way. Another filter or page is another entry, which has
  // no failure while it is asked for, so the last failure is also remembered until an answer
  // comes: without it the rows from before the failure stood in for that request.
  const [lastFailed, setLastFailed] = useState(false);

  useEffect(() => {
    if (error !== undefined) setLastFailed(true);
    else if (currentData !== undefined) setLastFailed(false);
  }, [error, currentData]);

  const failed = (error !== undefined && currentData === undefined) || (lastFailed && isFetching);
  const shown = failed ? undefined : data;

  const setFilters = (next: InspectionFilters) => setSearchParams(writeFilters(next), { replace: true });
  const setPage = (next: number) => setSearchParams(writeFilters(filters, next), { replace: true });

  // Counted, so that each clear mounts the search box anew: a word still waiting out its pause
  // would otherwise be committed over the clear, and the box would go on showing it.
  const [clears, setClears] = useState(0);

  const clearFilters = () => {
    setClears((count) => count + 1);
    setFilters(noFilters);
  };

  /**
   * Rows from the page or filters just left, under the ones asked for. Marked only once the
   * wait is long enough to notice: a response inside the delay — every one on a local stack,
   * most on a good network — swaps the rows before any indicator would have shown, and one
   * that flashed on for a few milliseconds would be a flicker of its own. It clears the
   * moment the rows land, not a delay later.
   */
  const isPaging = isFetching && !isLoading;
  const showPaging = useDebounce(isPaging, PAGING_INDICATOR_DELAY) && isPaging;

  // Only the first load takes the overlay: a page change swaps rows under a reader who is
  // already looking at the list, and covering it would be the loudest thing on the screen.
  useEffect(() => {
    (isLoading ? show : hide)(componentId);

    return () => hide(componentId);
  }, [componentId, hide, isLoading, show]);

  const total = shown?.meta.total ?? 0;
  const pages = shown?.meta.pages ?? 0;

  // A page past the end — a link kept from a longer list — goes to the last one there is.
  useEffect(() => {
    if (!isFetching && pages > 0 && page > pages) {
      setSearchParams(writeFilters(filters, pages), { replace: true });
    }
  }, [filters, isFetching, page, pages, setSearchParams]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
      <Header />

      <InspectionFilterBar
        filters={filters}
        onChange={setFilters}
        onClear={clearFilters}
        clears={clears}
        count={total}
      />

      {failed ? (
        <Paper variant="outlined" sx={{ p: 3, textAlign: 'center' }}>
          <Typography color="text.secondary">{t('Inspections_LoadFailed')}</Typography>
          <Button
            size="small"
            variant="outlined"
            color="inherit"
            disabled={isFetching}
            onClick={() => { void refetch(); }}
            sx={{ mt: 1.5 }}
          >
            {t('ReportPreview_Retry')}
          </Button>
        </Paper>
      ) : <RecentInspectionsList
        heading={false}
        data={shown?.data}
        loading={showPaging}
        // Before the first answer the list's own empty state stands under the loading overlay.
        empty={shown && filtered ? (
          <Paper
            variant="outlined"
            sx={{ p: 4, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, textAlign: 'center' }}
          >
            <Box sx={{
              width: 44, height: 44, mb: 0.5, borderRadius: '50%', display: 'flex', alignItems: 'center',
              justifyContent: 'center', backgroundColor: gray[800], border: `1px solid ${gray[700]}`, color: gray[400],
            }}>
              <SearchX size={20} />
            </Box>
            <Typography sx={{ fontWeight: 500 }}>{t('Inspections_Filter_NoMatch')}</Typography>
            <Typography variant="body2" color="text.secondary">{t('Inspections_Filter_NoMatchHint')}</Typography>
            <Button variant="outlined" size="small" onClick={clearFilters} sx={{ mt: 1 }}>
              {t('Inspections_Filter_Reset')}
            </Button>
          </Paper>
        ) : undefined}
      />}

      {/* No pager while the list failed: `pages` is read from what is shown, and nothing is. */}
      {pages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <Pagination
            count={pages}
            page={Math.min(page, pages)}
            onChange={(_, value) => setPage(value)}
            color="primary"
            shape="rounded"
          />
        </Box>
      )}
    </Box>
  );
};

export default InspectionsPage;
